import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { LuCalendarClock, LuPiggyBank, LuTrash2, LuUmbrella, LuWallet } from 'react-icons/lu';
import DataCardList from '../../components/data/DataCardList';
import DataTable from '../../components/data/DataTable';
import DataToolbar from '../../components/data/DataToolbar';
import FilterSheet from '../../components/data/FilterSheet';
import StatTiles from '../../components/data/StatTiles';
import TablePagination from '../../components/data/TablePagination';
import ToggleChip from '../../components/data/ToggleChip';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import useElementWidth from '../../hooks/useElementWidth';
import useMediaQuery from '../../hooks/useMediaQuery';
import usePagedList from '../../hooks/usePagedList';
import useTableColumns from '../../hooks/useTableColumns';
import useViewportFit from '../../hooks/useViewportFit';
import InsuranceFormModal from './insurance/InsuranceFormModal';
import useInsurances from './insurance/useInsurances';
import useInsuranceText from './insurance/insuranceText';
import { isExpiringSoon } from './insurance/insuranceUtils';
import {
  COLUMN_KEYS,
  DEFAULT_VISIBLE_COLUMNS,
  INSURANCE_COLUMNS,
  MIN_COLUMN_WIDTH,
  PINNED_COLUMNS,
  getFilterState,
} from './insurance/insuranceColumns';
import InsuranceCell, { InsuranceCardRow, useInsuranceCellTitle } from './insurance/parts/InsuranceCell';
import InsuranceDetailPane from './insurance/parts/InsuranceDetailPane';
import InsuranceHeader from './insurance/parts/InsuranceHeader';

const COLUMNS_STORAGE_KEY = 'insurance.tableColumns.v1';
const DOCK_PANEL_QUERY = '(min-width: 1440px)';
const FIT_BOTTOM_GAP = 24;
const FIT_MIN_HEIGHT = 400;
const COMPACT_LIST_WIDTH = 640;
const COMPACT_PAGE_SIZE = 10;

const CARD_SURFACE = 'tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-rounded-[1.75rem] tw-border tw-border-white/80 tw-bg-white/80 tw-shadow-[inset_0_1px_0_rgba(255,255,255,1),0_20px_50px_-30px_rgba(15,23,42,0.35)] dark:tw-border-white/10 dark:tw-bg-[#22262f]/80 dark:tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_20px_50px_-30px_rgba(0,0,0,0.9)]';

/**
 * Sigorta Yönetimi (Tailwind), built like the other data pages: tiles (BES
 * and "ending soon" filter), a table card that reaches at most the bottom of
 * the screen with header filters and a column menu, end dates coloured by
 * how soon they come, and the selected record's panel beside the table.
 * The data logic lives in useInsurances.
 */
const EmployeeInsurancePage = () => {
  const {
    insurances, employees, isLoading, filteredInsurances, stats,
    selectedInsurance, setSelectedInsurance, filters, handleFilter, clearFilters,
    searchTerm, setSearchTerm, showExpiring, setShowExpiring, onlyBES, setOnlyBES,
    deleteInsurance, downloadExcel, refresh,
  } = useInsurances();
  const { language, locale, label, formatMoney } = useInsuranceText();
  const getCellTitle = useInsuranceCellTitle();

  const columns = useTableColumns({
    storageKey: COLUMNS_STORAGE_KEY,
    columns: INSURANCE_COLUMNS,
    defaultVisible: DEFAULT_VISIBLE_COLUMNS,
    minWidth: MIN_COLUMN_WIDTH,
  });
  const dockPanel = useMediaQuery(DOCK_PANEL_QUERY);
  const listCardRef = useRef(null);
  const compactList = useElementWidth(listCardRef, window.innerWidth) < COMPACT_LIST_WIDTH;
  const tableAreaRef = useRef(null);
  const fitHeight = useViewportFit(tableAreaRef, { bottomGap: FIT_BOTTOM_GAP, minHeight: FIT_MIN_HEIGHT });
  // Sayfa başına sabit 10 satır (kullanıcı isteği): kart ekrana sığdırılmaz, tablo satır sayısını ölçmez
  const maxCardHeight = undefined;
  const [showFilterSheet, setShowFilterSheet] = useState(false);
  const [form, setForm] = useState({ open: false, insurance: null, key: 0 });
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const hasLoaded = insurances.length > 0 || !isLoading;

  // Süzgeç ya da arama değişince ilk sayfaya dönülür
  const resetKey = JSON.stringify([filters, searchTerm, showExpiring, onlyBES]);
  const { pageRows, pageStart, currentPage, totalPages, pageSize, setPageSize, goToPage, reveal } = usePagedList(filteredInsurances, { resetKey });

  useEffect(() => {
    if (compactList) setPageSize(COMPACT_PAGE_SIZE);
  }, [compactList, setPageSize]);

  // Escape closes the panel, unless a dialog or field has it
  useEffect(() => {
    if (!selectedInsurance) return undefined;
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape' || event.defaultPrevented || showFilterSheet) return;
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target?.tagName)) return;
      setSelectedInsurance(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedInsurance, showFilterSheet, setSelectedInsurance]);

  const columnLabel = useCallback((key) => label(`columns.${INSURANCE_COLUMNS[key].id}`), [label]);
  const besOptions = useMemo(() => [{ value: 'true', label: label('bes.yes') }, { value: 'false', label: label('bes.no') }], [label]);

  const tableColumns = useMemo(() => columns.visibleKeys.map((key) => ({
    key,
    label: columnLabel(key),
    filter: { type: INSURANCE_COLUMNS[key].type, options: key === 'hasBES' ? besOptions : undefined },
  })), [columns.visibleKeys, columnLabel, besOptions]);

  const chipValue = (key) => {
    if (key === 'hasBES') return filters[key] === 'true' ? label('bes.yes') : label('bes.no');
    return String(filters[key]);
  };
  const filterChips = Object.keys(filters)
    .filter((key) => INSURANCE_COLUMNS[key] && getFilterState(key, filters[key]) !== 'off')
    .map((key) => ({ key, label: columnLabel(key), value: chipValue(key), state: 'applied' }));

  const isFilterActive = Object.values(filters).some(Boolean) || Boolean(searchTerm) || showExpiring || onlyBES;

  // "Sayfaya git": 1 ile son sayfa arasında olmalı
  const handlePageJump = (text) => {
    const target = parseInt(text, 10);
    if (!Number.isFinite(target) || target < 1 || target > totalPages) {
      toast.error(label('toast.pageJumpInvalid', { max: totalPages }));
      return false;
    }
    goToPage(target);
    return true;
  };

  const selectedIndex = selectedInsurance ? filteredInsurances.findIndex((insurance) => insurance.id === selectedInsurance.id) : -1;
  const selectSibling = (offset) => {
    const sibling = filteredInsurances[selectedIndex + offset];
    if (!sibling) return;
    setSelectedInsurance(sibling);
    reveal(sibling.id);
  };

  const openForm = (insurance = null) => setForm((current) => ({ open: true, insurance, key: current.key + 1 }));
  const closeForm = () => setForm((current) => ({ ...current, open: false }));

  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    const ok = await deleteInsurance(toDelete);
    setDeleting(false);
    if (ok) setToDelete(null);
  };

  const total = stats.total;
  const shareOf = (count) => (total > 0 ? Math.min(1, count / total) : 0);
  const shareTitle = (count) => label('stats.share', { percent: Math.round(shareOf(count) * 100) });
  const tiles = [
    { key: 'total', label: label('stats.total'), shortLabel: label('stats.totalShort'), value: total, icon: LuUmbrella, gradient: 'tw-from-rose-400 tw-to-pink-600 tw-shadow-rose-500/30', onClick: clearFilters, active: !isFilterActive },
    { key: 'bes', label: label('stats.withBES'), shortLabel: label('stats.withBESShort'), value: stats.withBES, icon: LuPiggyBank, gradient: 'tw-from-emerald-400 tw-to-teal-600 tw-shadow-emerald-500/30', share: shareOf(stats.withBES), shareTitle: shareTitle(stats.withBES), barClass: 'tw-bg-gradient-to-r tw-from-emerald-400 tw-to-teal-500', onClick: () => setOnlyBES(!onlyBES), active: onlyBES },
    { key: 'expiring', label: label('stats.expiring'), shortLabel: label('stats.expiringShort'), value: stats.expiring, icon: LuCalendarClock, gradient: 'tw-from-amber-400 tw-to-orange-500 tw-shadow-amber-500/30', onClick: () => setShowExpiring(!showExpiring), active: showExpiring },
    { key: 'cost', label: label('stats.totalCost'), shortLabel: label('stats.totalCostShort'), value: stats.totalCost, format: (value) => formatMoney(value).replace(' ₺', ''), suffix: '₺', icon: LuWallet, gradient: 'tw-from-sky-400 tw-to-blue-600 tw-shadow-blue-500/30' },
  ];

  const quickFilters = (
    <>
      <ToggleChip pressed={showExpiring} onChange={setShowExpiring} icon={LuCalendarClock} label={label('toolbar.expiring')} title={label('toolbar.expiringHint')} count={hasLoaded ? stats.expiring : undefined} tone="amber" />
      <ToggleChip pressed={onlyBES} onChange={setOnlyBES} icon={LuPiggyBank} label={label('toolbar.bes')} title={label('toolbar.besHint')} count={hasLoaded ? stats.withBES : undefined} tone="teal" />
    </>
  );

  const paneProps = {
    insurance: selectedInsurance,
    position: selectedIndex >= 0 ? { index: selectedIndex, total: filteredInsurances.length } : null,
    onPrev: selectedIndex > 0 ? () => selectSibling(-1) : undefined,
    onNext: selectedIndex >= 0 && selectedIndex < filteredInsurances.length - 1 ? () => selectSibling(1) : undefined,
    onClose: () => setSelectedInsurance(null),
    onEdit: () => openForm(selectedInsurance),
    onDelete: () => setToDelete(selectedInsurance),
  };

  return (
    <>
      <div lang={language} className="tw-mx-auto tw-w-full tw-max-w-[1680px] tw-space-y-4 tw-font-sans tw-antialiased sm:tw-space-y-5 md:tw-px-4 lg:tw-px-6 2xl:tw-px-10">
        <InsuranceHeader onAdd={() => openForm(null)} onExcel={downloadExcel} />

        <StatTiles tiles={tiles} ready={hasLoaded} className="tw-grid-cols-2 lg:tw-grid-cols-4" />

        <div ref={tableAreaRef} className="tw-flex tw-items-start tw-gap-4">
          <section ref={listCardRef} aria-label={label('table.listLabel')} style={{ maxHeight: maxCardHeight }} className={CARD_SURFACE}>
            <DataToolbar
              search={{
                value: searchTerm,
                onChange: setSearchTerm,
                placeholder: label('toolbar.search'),
                placeholderShort: label('toolbar.searchShort'),
                label: label('toolbar.search'),
                minLength: 1,
              }}
              columnMenu={{
                columns: COLUMN_KEYS.map((key) => ({ key, label: columnLabel(key) })),
                visibleColumns: columns.visibleColumns,
                onToggle: columns.toggleColumn,
                onShowAll: columns.showAllColumns,
                onReset: columns.resetColumns,
              }}
              compact={compactList}
              onOpenFilters={() => setShowFilterSheet(true)}
              chips={filterChips}
              onRemoveChip={(key) => handleFilter(key, '')}
              onClearAll={clearFilters}
              extra={quickFilters}
            />

            {compactList ? (
              <DataCardList
                label={label('table.listLabel')}
                rows={pageRows}
                renderCard={(insurance) => <InsuranceCardRow insurance={insurance} />}
                selectedId={selectedInsurance?.id}
                onSelect={setSelectedInsurance}
                isLoading={isLoading}
                isBusy={false}
                emptyLabel={label('table.empty')}
                canClearFilters={isFilterActive}
                onClearFilters={clearFilters}
              />
            ) : (
              <DataTable
                label={label('table.listLabel')}
                rows={pageRows}
                columns={tableColumns}
                getColumnWidth={columns.getColumnWidth}
                onResizeColumn={columns.setColumnWidth}
                onResetColumnWidth={columns.resetColumnWidth}
                pinnedKeys={PINNED_COLUMNS}
                filters={filters}
                onFilterChange={handleFilter}
                getFilterState={getFilterState}
                renderCell={(insurance, key) => <InsuranceCell columnKey={key} insurance={insurance} />}
                getCellTitle={getCellTitle}
                getRowTone={(insurance) => (isExpiringSoon(insurance) ? 'danger' : undefined)}
                selectedId={selectedInsurance?.id}
                onSelect={setSelectedInsurance}
                isLoading={isLoading}
                isBusy={false}
                skeletonRows={pageSize}
                maxCardHeight={maxCardHeight}
                onCapacityChange={setPageSize}
                emptyLabel={label('table.empty')}
                canClearFilters={isFilterActive}
                onClearFilters={clearFilters}
                onShowColumns={columns.resetColumns}
              />
            )}

            <TablePagination
              rangeStart={pageStart + 1}
              rangeEnd={pageStart + pageRows.length}
              resultCount={filteredInsurances.length}
              rangeOf={label('table.rangeOf', { total: filteredInsurances.length.toLocaleString(locale) })}
              ofTotal={isFilterActive ? label('table.ofTotal', { total: total.toLocaleString(locale) }) : undefined}
              currentPage={currentPage}
              totalPages={totalPages}
              isLoading={false}
              compact={compactList}
              onPage={goToPage}
              jump={{ label: label('table.jump'), onJump: handlePageJump }}
            />
          </section>

          {dockPanel && <InsuranceDetailPane docked height={fitHeight} {...paneProps} />}
        </div>
      </div>

      {!dockPanel && <InsuranceDetailPane {...paneProps} />}

      <FilterSheet
        isOpen={showFilterSheet}
        onClose={() => setShowFilterSheet(false)}
        fields={COLUMN_KEYS.map((key) => ({ key, label: columnLabel(key), type: INSURANCE_COLUMNS[key].type, options: key === 'hasBES' ? besOptions : undefined }))}
        filters={filters}
        getFilterState={getFilterState}
        onFilterChange={handleFilter}
        onClearAll={clearFilters}
        resultCount={filteredInsurances.length}
      />

      <InsuranceFormModal
        key={form.key}
        isOpen={form.open}
        insurance={form.insurance}
        employees={employees}
        onClose={closeForm}
        onSaved={refresh}
      />

      <ConfirmDialog
        isOpen={Boolean(toDelete)}
        title={label('delete.title')}
        message={label('delete.message', { name: toDelete?.employeeName || '' })}
        confirmLabel={label('delete.confirm')}
        cancelLabel={label('delete.cancel')}
        tone="danger"
        icon={LuTrash2}
        busy={deleting}
        onConfirm={confirmDelete}
        onCancel={() => !deleting && setToDelete(null)}
      />
    </>
  );
};

export default EmployeeInsurancePage;
